import React, { useState, useMemo } from "react"
import {
    View,
    TouchableOpacity,
    TextInput,
    ActivityIndicator,
} from "react-native"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

const ChapterAutocomplete = ({
    chapters = [],
    selectedChapterIds = [],
    onToggleChapter,
    onAddCustomChapter,
    loading = false,
    disabled = false,
}) => {
    const [searchQuery, setSearchQuery] = useState("")

    // Filter chapters based on search query
    const filteredChapters = useMemo(() => {
        if (!searchQuery.trim()) return chapters
        const query = searchQuery.toLowerCase().trim()
        return chapters.filter((chapter) =>
            chapter.name.toLowerCase().includes(query)
        )
    }, [chapters, searchQuery])

    // Group filtered chapters by parent
    const groupedChapters = useMemo(() => {
        const chaptersWithParent = filteredChapters.filter((c) => c.parent_chapter)
        const chaptersWithoutParent = filteredChapters.filter((c) => !c.parent_chapter)

        const grouped = {}
        chaptersWithParent.forEach((chapter) => {
            const parentId = chapter.parent_chapter.id
            const parentName = chapter.parent_chapter.name
            if (!grouped[parentId]) {
                grouped[parentId] = {
                    name: parentName,
                    chapters: [],
                }
            }
            grouped[parentId].chapters.push(chapter)
        })

        return { grouped, ungrouped: chaptersWithoutParent }
    }, [filteredChapters])

    // Get selected chapters for display as chips
    const selectedChapters = useMemo(() => {
        return chapters.filter((c) => selectedChapterIds.includes(c.id))
    }, [chapters, selectedChapterIds])

    const hasSearchQuery = searchQuery.trim().length > 0
    const showCreateOption = hasSearchQuery && onAddCustomChapter

    const renderChapterItem = (chapter) => {
        const isSelected = selectedChapterIds.includes(chapter.id)
        return (
            <TouchableOpacity
                key={chapter.id}
                onPress={() => onToggleChapter(chapter.id)}
                disabled={disabled}
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 8,
                    backgroundColor: isSelected
                        ? theme.colors.primaryLighter
                        : theme.colors.lightMore,
                    borderWidth: isSelected ? 1 : 0,
                    borderColor: theme.colors.primary,
                    opacity: disabled ? 0.5 : 1,
                }}
            >
                <FontAwesome5
                    name={isSelected ? "check-square" : "square"}
                    size={18}
                    color={isSelected ? theme.colors.primary : theme.colors.grey}
                    solid={isSelected}
                />
                <TextM fontSize={14} style={{ marginLeft: 10, flex: 1 }} wrap>
                    {chapter.name}
                </TextM>
            </TouchableOpacity>
        )
    }

    return (
        <View>
            {/* Selected chapters as chips */}
            {selectedChapters.length > 0 && (
                <View
                    style={{
                        flexDirection: "row",
                        flexWrap: "wrap",
                        marginBottom: 12,
                    }}
                >
                    {selectedChapters.map((chapter) => (
                        <TouchableOpacity
                            key={chapter.id}
                            onPress={() => onToggleChapter(chapter.id)}
                            disabled={disabled}
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                backgroundColor: theme.colors.primaryLighter,
                                borderRadius: 16,
                                paddingHorizontal: 12,
                                paddingVertical: 6,
                                marginRight: 8,
                                marginBottom: 8,
                                borderWidth: 1,
                                borderColor: theme.colors.primary,
                            }}
                        >
                            <TextM
                                fontSize={12}
                                fontWeight="SemiBold"
                                color={theme.colors.primary}
                                numberOfLines={1}
                                style={{ maxWidth: 150 }}
                            >
                                {chapter.name}
                            </TextM>
                            <FontAwesome5
                                name="times"
                                size={12}
                                color={theme.colors.primary}
                                style={{ marginLeft: 6 }}
                            />
                        </TouchableOpacity>
                    ))}
                </View>
            )}

            {/* Search input */}
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    borderWidth: 1,
                    borderColor: theme.colors.lightMore,
                    borderRadius: 10,
                    paddingHorizontal: 12,
                    backgroundColor: disabled ? theme.colors.lighter : theme.colors.white,
                    marginBottom: 12,
                }}
            >
                <FontAwesome5
                    name="search"
                    size={14}
                    color={theme.colors.grey}
                />
                <TextInput
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    placeholder="Rechercher un chapitre..."
                    placeholderTextColor={theme.colors.grey}
                    editable={!disabled && !loading}
                    style={{
                        flex: 1,
                        paddingVertical: 12,
                        paddingHorizontal: 10,
                        fontSize: 14,
                        fontFamily: "Montserrat",
                        color: theme.colors.dark,
                    }}
                />
                {loading && (
                    <ActivityIndicator
                        size="small"
                        color={theme.colors.primary}
                    />
                )}
            </View>

            {/* Loading state */}
            {loading && (
                <View style={{ padding: 20, alignItems: "center" }}>
                    <ActivityIndicator color={theme.colors.primary} />
                    <TextM
                        fontSize={13}
                        color={theme.colors.grey}
                        style={{ marginTop: 10 }}
                    >
                        Chargement des chapitres...
                    </TextM>
                </View>
            )}

            {/* Chapter list */}
            {!loading && (
                <>
                    {/* Grouped chapters */}
                    {Object.entries(groupedChapters.grouped).map(([parentId, group]) => (
                        <View key={parentId} style={{ marginBottom: 15 }}>
                            <TextM
                                fontSize={12}
                                fontWeight="SemiBold"
                                color={theme.colors.grey}
                                style={{ marginBottom: 8 }}
                            >
                                {group.name}
                            </TextM>
                            {group.chapters.map(renderChapterItem)}
                        </View>
                    ))}

                    {/* Ungrouped chapters */}
                    {groupedChapters.ungrouped.length > 0 && (
                        <View style={{ marginBottom: 15 }}>
                            {Object.keys(groupedChapters.grouped).length > 0 && (
                                <TextM
                                    fontSize={12}
                                    fontWeight="SemiBold"
                                    color={theme.colors.grey}
                                    style={{ marginBottom: 8 }}
                                >
                                    Autres
                                </TextM>
                            )}
                            {groupedChapters.ungrouped.map(renderChapterItem)}
                        </View>
                    )}

                    {/* Add custom chapter option */}
                    {showCreateOption && (
                        <TouchableOpacity
                            onPress={() => {
                                onAddCustomChapter(searchQuery.trim())
                                setSearchQuery("")
                            }}
                            disabled={disabled}
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                padding: 12,
                                borderRadius: 8,
                                marginBottom: 8,
                                backgroundColor: theme.colors.primaryLighter,
                                borderWidth: 1,
                                borderColor: theme.colors.primary,
                            }}
                        >
                            <FontAwesome5
                                name="plus"
                                size={14}
                                color={theme.colors.primary}
                            />
                            <TextM
                                fontSize={14}
                                fontWeight="SemiBold"
                                color={theme.colors.primary}
                                style={{ marginLeft: 10 }}
                            >
                                Ajouter "{searchQuery.trim()}"
                            </TextM>
                        </TouchableOpacity>
                    )}
                </>
            )}
        </View>
    )
}

export default ChapterAutocomplete
